import React from 'react';
import { ArrowLeft } from 'lucide-react';

const TermsOfService: React.FC = () => {
  return (
    <div className="font-manrope min-h-screen bg-gradient-to-b from-white to-[#3C59B2]/10">
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <a href="/" className="inline-block flex items-center space-x-2 text-gray-600 hover:text-gray-900 mb-8">
          <ArrowLeft className="w-4 h-4" />
          <span>Back to home</span>
        </a>

        <h1 className="font-manrope text-4xl font-bold text-gray-900 mb-8 animate-fade-in-up">Terms of Service</h1>
        <p className="text-gray-600 mb-8">Last updated: {new Date().toLocaleDateString('fr-FR')}</p>

        <div className="prose prose-lg max-w-none space-y-8">
          <section>
            <h2 className="font-manrope text-2xl font-bold text-gray-900 mb-4">1. Agreement to Terms</h2>
            <p className="text-gray-700">
              By accessing or using 3DtoVideo services operated by MON SERVICE IMMO, you agree to be bound by these 
              Terms of Service and our Privacy Policy. If you do not agree to these terms, do not use our services.
            </p>
          </section>

          <section>
            <h2 className="font-manrope text-2xl font-bold text-gray-900 mb-4">2. Service Description</h2>
            <p className="text-gray-700">
              3DtoVideo provides video production services converting Matterport tours and photos into professional 
              marketing videos and property websites. Our services include:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mt-4">
              <li>5 professional videos per property</li>
              <li>Dedicated property website</li>
              <li>24-hour delivery (Monday-Thursday submissions)</li>
              <li>Subscription-based pricing plans</li>
            </ul>
          </section>

          <section>
            <h2 className="font-manrope text-2xl font-bold text-gray-900 mb-4">3. Account Registration</h2>
            <p className="text-gray-700 mb-4">To use our services, you must:</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Be at least 18 years old</li>
              <li>Provide accurate and complete information</li>
              <li>Maintain the security of your account credentials</li>
              <li>Notify us immediately of any unauthorized access</li>
            </ul>
            <p className="text-gray-700 mt-4">
              You are responsible for all activities that occur under your account.
            </p>
          </section>

          <section>
            <h2 className="font-manrope text-2xl font-bold text-gray-900 mb-4">4. Subscription and Payment</h2>
            <p className="text-gray-700 mb-4"><strong>Subscription Plans:</strong></p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Starter: 5 properties/month at €800/month</li>
              <li>Professional: 10 properties/month at €1,520/month</li>
              <li>Enterprise: 20 properties/month at €2,880/month</li>
            </ul>
            <p className="text-gray-700 mt-4"><strong>Payment Terms:</strong></p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mt-2">
              <li>Subscriptions are billed monthly in advance</li>
              <li>Payments are processed through Stripe</li>
              <li>All prices are in EUR and exclude applicable taxes</li>
              <li>Failed payments may result in service suspension</li>
            </ul>
          </section>

          <section>
            <h2 className="font-manrope text-2xl font-bold text-gray-900 mb-4">5. Cancellation and Refunds</h2>
            <p className="text-gray-700 mb-4"><strong>Cancellation:</strong></p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>You may cancel your subscription at any time</li>
              <li>Cancellation takes effect at the end of the current billing period</li>
              <li>No refunds for partial months</li>
              <li>Unused property slots do not roll over to the next month</li>
            </ul>
            <p className="text-gray-700 mt-4"><strong>Refund Policy:</strong></p>
            <p className="text-gray-700">
              Refunds are provided only in cases of service failure on our part. We do not provide refunds 
              for change of mind or unused services.
            </p>
          </section>

          <section>
            <h2 className="font-manrope text-2xl font-bold text-gray-900 mb-4">6. Delivery Terms</h2>
            <p className="text-gray-700 mb-4">
              <strong>24-hour delivery guarantee:</strong> Properties submitted Monday through Thursday will be 
              delivered within 24 business hours. Submissions on Friday, Saturday, or Sunday will be processed 
              on the following Monday.
            </p>
            <p className="text-gray-700">
              Delivery times may be affected by holidays, technical issues, or force majeure events.
            </p>
          </section>

          <section>
            <h2 className="font-manrope text-2xl font-bold text-gray-900 mb-4">7. Content and Intellectual Property</h2>
            <p className="text-gray-700 mb-4"><strong>Your Content:</strong></p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>You retain ownership of all content you provide (photos, Matterport tours)</li>
              <li>You grant us a license to use your content to provide our services</li>
              <li>You represent that you have the rights to all content you submit</li>
            </ul>
            <p className="text-gray-700 mt-4"><strong>Our Content:</strong></p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Delivered videos and websites remain your property for commercial use</li>
              <li>We retain the right to use deliverables in our portfolio and marketing</li>
              <li>Our templates, processes, and branding remain our intellectual property</li>
            </ul>
          </section>

          <section>
            <h2 className="font-manrope text-2xl font-bold text-gray-900 mb-4">8. Acceptable Use</h2>
            <p className="text-gray-700 mb-4">You agree not to:</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Use our services for illegal purposes</li>
              <li>Submit content that infringes third-party rights</li>
              <li>Submit offensive, defamatory, or inappropriate content</li>
              <li>Attempt to reverse-engineer or copy our processes</li>
              <li>Share account credentials with others</li>
              <li>Abuse or overload our systems</li>
            </ul>
          </section>

          <section>
            <h2 className="font-manrope text-2xl font-bold text-gray-900 mb-4">9. Limitation of Liability</h2>
            <p className="text-gray-700">
              To the maximum extent permitted by law, MON SERVICE IMMO shall not be liable for any indirect, 
              incidental, special, consequential, or punitive damages, or any loss of profits or revenues, 
              whether incurred directly or indirectly, or any loss of data, use, goodwill, or other intangible losses.
            </p>
            <p className="text-gray-700 mt-4">
              Our total liability shall not exceed the amount you paid us in the 12 months preceding the claim.
            </p>
          </section>

          <section>
            <h2 className="font-manrope text-2xl font-bold text-gray-900 mb-4">10. Warranties Disclaimer</h2>
            <p className="text-gray-700">
              Our services are provided "as is" without warranties of any kind, either express or implied. 
              We do not guarantee uninterrupted or error-free service.
            </p>
          </section>

          <section>
            <h2 className="font-manrope text-2xl font-bold text-gray-900 mb-4">11. Modifications to Service</h2>
            <p className="text-gray-700">
              We reserve the right to modify, suspend, or discontinue any part of our service at any time. 
              We will provide reasonable notice of significant changes.
            </p>
          </section>

          <section>
            <h2 className="font-manrope text-2xl font-bold text-gray-900 mb-4">12. Governing Law</h2>
            <p className="text-gray-700">
              These Terms shall be governed by and construed in accordance with the laws of France. 
              Any disputes shall be subject to the exclusive jurisdiction of the courts of France.
            </p>
          </section>

          <section>
            <h2 className="font-manrope text-2xl font-bold text-gray-900 mb-4">13. Contact Information</h2>
            <p className="text-gray-700">
              For questions about these Terms, contact us at:<br />
              <strong>MON SERVICE IMMO</strong><br />
              38 rue Victor Hugo<br />
              42300 Mably, France<br />
              Email: contact@3dtovideo.com
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;